import type { PlaceOrderInput } from '@repo/api-contracts';
import { useNavigate } from 'react-router';
import { useInvalidateMyOrders } from '@/entities/order';
import { useInvalidateMyQuotes } from '@/entities/quote';
import { trpc } from '@/shared/api';
import { paymentCheckoutPath } from '@/shared/config';
import { getTrpcErrorCode, orderErrorCodeOf } from '@/shared/lib';

export interface UsePlaceOrderResult {
  /** 실패하면 서버 오류를 그대로 던진다 — 문구는 주문하기 폼이 정한다(입력·동의 유지). */
  readonly place: (input: PlaceOrderInput) => Promise<void>;
  readonly isPending: boolean;
}

/**
 * 주문하기 제출(orders.place, 견적 설계 §7-5). 성공하면 견적(ORDERED)·내 주문 목록을 다시 불러오고, 그 견적의 주문하기 화면
 * 데이터는 낡은 것으로만 표시한 뒤(이동 중 화면이 바뀌지 않게) 새 주문의 결제 페이지로 이동한다. 상태 충돌(CONFLICT — 최신
 * 발행본 아님·살아 있는 주문·상태 변경)이나 만료(QUOTE_EXPIRED)면 주문하기 화면과 견적 상세를 다시 불러와 낡은 발행본으로
 * 다시 제출하지 않게 한다.
 */
export const usePlaceOrder = (): UsePlaceOrderResult => {
  const navigate = useNavigate();
  const orders = useInvalidateMyOrders();
  const quotes = useInvalidateMyQuotes();
  const mutation = trpc.orders.place.useMutation();

  const place = async (input: PlaceOrderInput): Promise<void> => {
    try {
      const { orderId } = await mutation.mutateAsync(input);
      await Promise.all([
        quotes.detail(input.quoteRequestId),
        quotes.list(),
        orders.list(),
        orders.expirePlacement(input.quoteRequestId),
      ]);
      void navigate(paymentCheckoutPath(orderId));
    } catch (failure) {
      if (
        getTrpcErrorCode(failure) === 'CONFLICT' ||
        orderErrorCodeOf(failure) === 'QUOTE_EXPIRED'
      ) {
        await Promise.all([
          orders.placement(input.quoteRequestId),
          quotes.detail(input.quoteRequestId),
        ]);
      }
      throw failure;
    }
  };

  return { place, isPending: mutation.isPending };
};
